import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldStar, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      setSent(true);
      toast.success("You're subscribed. Thanks!");
    } catch (err) {
      toast.error("Couldn't subscribe — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-[#0A1128] text-white mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-9 w-9 rounded-md bg-white text-[#0033A0] flex items-center justify-center">
              <ShieldStar size={20} weight="fill" />
            </span>
            <span className="font-display font-black text-lg tracking-tight">
              Boot<span className="text-[#D90429]">To</span>Benefit
            </span>
          </div>
          <p className="text-slate-300 text-sm max-w-md leading-relaxed">
            Plain-language guidance on military benefits, career paths, and post-service options — for recruits, service members, and their families.
          </p>

          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md" data-testid="newsletter-form">
            <div className="flex-1 flex items-center bg-white/5 border border-white/15 rounded-md px-3">
              <EnvelopeSimple size={18} className="text-slate-300" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="newsletter-email-input"
                className="bg-transparent flex-1 px-2 py-2.5 text-sm placeholder:text-slate-400 outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={busy || sent}
              data-testid="newsletter-submit"
              className="inline-flex items-center justify-center gap-2 bg-[#D90429] hover:bg-[#B00020] disabled:opacity-60 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors"
            >
              {sent ? (<><CheckCircle size={16} weight="fill" /> Subscribed</>) : "Get updates"}
            </button>
          </form>
          <p className="text-xs text-slate-400 mt-2">Monthly. Unsubscribe anytime. No spam, ever.</p>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mb-4">Learn</h4>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/start-here" className="hover:text-white">Start Here</Link></li>
            <li><Link to="/quiz" className="hover:text-white">Job Quiz</Link></li>
            <li><Link to="/topics" className="hover:text-white">All Topics</Link></li>
            <li><Link to="/branches" className="hover:text-white">Branches</Link></li>
            <li><Link to="/compare" className="hover:text-white">Compare</Link></li>
            <li><Link to="/career-deepdive" className="hover:text-white">Career Deep Dive</Link></li>
            <li><Link to="/donate" className="hover:text-white">Donate</Link></li>
            <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mb-4">Benefits</h4>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/topics/pay" className="hover:text-white">Pay</Link></li>
            <li><Link to="/topics/housing" className="hover:text-white">Housing & BAH</Link></li>
            <li><Link to="/topics/education" className="hover:text-white">GI Bill</Link></li>
            <li><Link to="/topics/tsp" className="hover:text-white">TSP</Link></li>
            <li><Link to="/topics/va-loan" className="hover:text-white">VA Home Loan</Link></li>
            <li><Link to="/topics/healthcare" className="hover:text-white">TRICARE</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mb-4">Official resources</h4>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><a href="https://www.va.gov" target="_blank" rel="noreferrer" className="hover:text-white">VA.gov</a></li>
            <li><a href="https://www.tricare.mil" target="_blank" rel="noreferrer" className="hover:text-white">TRICARE.mil</a></li>
            <li><a href="https://www.tsp.gov" target="_blank" rel="noreferrer" className="hover:text-white">TSP.gov</a></li>
            <li><a href="https://skillbridge.osd.mil" target="_blank" rel="noreferrer" className="hover:text-white">DoD SkillBridge</a></li>
            <li><a href="https://www.todaysmilitary.com" target="_blank" rel="noreferrer" className="hover:text-white">Today{`'`}s Military</a></li>
          </ul>
          <h4 className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mt-6 mb-2">Contact</h4>
          <a href="mailto:boottobenefit.contact@gmail.com" className="text-sm text-slate-300 hover:text-white break-all" data-testid="footer-contact-email">
            boottobenefit.contact@gmail.com
          </a>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 text-xs text-slate-400">
          <p>© {new Date().getFullYear()} Boot To Benefit. Not affiliated with the U.S. Department of Defense.</p>
          <p>Information is educational. Always confirm specifics with official sources or your recruiter.</p>
        </div>
      </div>
    </footer>
  );
}
